export const BREADTH_MARKETS = ['nasdaq', 'nyse', 'combined']
const STATUSES = new Set(['ready', 'ready_with_history_gaps', 'unavailable', 'collection_failed', 'stale', 'incomplete', 'history_gap', 'warming_up'])
const SYMBOLS = ['INDEX:HIGQ', 'INDEX:LOWQ', 'INDEX:HIGN', 'INDEX:LOWN']
const validDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value
const validTime = (value) => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value))

function readRow(row) {
  if (!validDay(row?.session) || typeof row.complete !== 'boolean') throw new Error('Invalid breadth session')
  const result = { session: row.session, complete: row.complete }
  for (const market of BREADTH_MARKETS) {
    const { highs, lows, net, regime } = row[market] || {}
    if (![highs, lows].every((v) => v === null || Number.isSafeInteger(v) && v >= 0)
      || !(net === null || Number.isSafeInteger(net) && highs !== null && lows !== null && net === highs - lows)
      || !(regime === null || [-1, 0, 1].includes(regime))
      || row.complete && [highs, lows, net, regime].some((v) => v === null)) throw new Error('Invalid breadth counts')
    result[market] = { highs, lows, net, regime }
  }
  if (row.complete && ['highs', 'lows', 'net'].some((key) => result.combined[key] !== result.nasdaq[key] + result.nyse[key])) throw new Error('Invalid combined breadth')
  return result
}

// Select only public data. Provider messages, credentials and unknown fields
// never cross this boundary. Regimes come from the canonical Engine collector.
export function readBreadthSnapshot(source, now = Date.now()) {
  if (source?.schema_version !== 1 || source.provider !== 'tradingview_mcp'
    || !STATUSES.has(source.status) || typeof source.usable !== 'boolean'
    || typeof source.actionable !== 'boolean' || !Array.isArray(source.history)
    || source.history.length > 5000) throw new Error('Invalid breadth snapshot')
  if (source.status === 'unavailable' && !source.history.length && source.latest === null && !source.usable) {
    return { schema_version: 1, provider: 'tradingview_mcp', status: 'unavailable', usable: false, actionable: false, history: [], latest: null }
  }
  if (JSON.stringify(source.symbols) !== JSON.stringify(SYMBOLS) || !validTime(source.as_of)
    || !validDay(source.expected_session) || source.collected_at != null && !validTime(source.collected_at)) throw new Error('Invalid breadth provenance')
  const age = now - Date.parse(source.as_of)
  if (age < -5000) throw new Error('Future breadth snapshot')
  const history = source.history.map(readRow)
  if (history.some((row, index) => row.session > source.expected_session || index && row.session <= history[index - 1].session)) throw new Error('Invalid breadth ordering')
  const latest = source.latest ? readRow(source.latest) : null
  if (JSON.stringify(latest) !== JSON.stringify(history.at(-1) || null)) throw new Error('Invalid latest breadth')
  if (source.usable && (!latest?.complete || latest.session !== source.expected_session || !source.status.startsWith('ready'))) throw new Error('Invalid breadth readiness')
  const usable = source.usable && age <= 120000
  return { schema_version: 1, provider: source.provider, symbols: SYMBOLS, as_of: source.as_of,
    collected_at: source.collected_at || null, expected_session: source.expected_session,
    status: age > 120000 ? 'stale' : source.status, usable, actionable: usable && source.actionable, latest, history }
}
